import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AdvanceSettings from "./AdvanceSettings";
import { SNETButton, SNETTextfield, TagsInputField, alertTypes } from "shared/dist/components";
import { organizationActions } from "../../../../Services/Redux/actionCreators";
import validator from "shared/dist/utils/validator";
import { orgSetupRegionValidationConstraints } from "../validationConstraints";
import classes from "./Settings.module.css";

const Settings = ({ groups, group, groupIndex, foundInBlockchain }) => {
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const [endpointValidation, setEndpointValidation] = useState();
    const [paymentAddressValidation, setPaymentAddressValidation] = useState();

    const dispatch = useDispatch();

    const { name, paymentAddress, paymentConfig } = group;

    const handlePaymentAddressChange = (event) => {
        const { value } = event.target;
        handlePaymentAddressValidation(value);
        const updatedGroups = [...groups];
        updatedGroups[groupIndex] = { ...group, paymentAddress: value };
        dispatch(organizationActions.setGroups(updatedGroups));
    };

    const handlePaymentAddressValidation = (value) => {
        const invalidPaymentAddressMessage = validator.single(
            value,
            orgSetupRegionValidationConstraints.groups.array.paymentAddress
        );
        if (invalidPaymentAddressMessage) {
            setPaymentAddressValidation({
                type: alertTypes.ERROR,
                message: `${value}  is not a valid endpoint`,
            });
        } else {
            setPaymentAddressValidation();
        }
    };

    const updateEndpointsInGroup = (groupsToBeUpdated, updatedEndpoints) => {
        groupsToBeUpdated[groupIndex] = {
            ...group,
            paymentConfig: {
                ...group.paymentConfig,
                paymentChannelStorageClient: {
                    ...group.paymentConfig.paymentChannelStorageClient,
                    endpoints: updatedEndpoints,
                },
            },
        };
        return groupsToBeUpdated;
    };

    const handleEndPointValidation = (value) => {
        const invalidEndPointMessage = validator.validators.validURL(value, {
            message: `${value} is not a valid endpoint`,
        });
        if (invalidEndPointMessage) {
            setEndpointValidation({ type: alertTypes.ERROR, message: invalidEndPointMessage });
            return false;
        }
        setEndpointValidation();
        return true;
    };

    const handleKeyEnterInTags = (newEndpoints) => {
        const updatedEndpoints = [];
        let updatedGroups = [...groups];

        newEndpoints.forEach((endpoint) => {
            const isEndpointValid = handleEndPointValidation(endpoint);
            if (isEndpointValid) {
                updatedEndpoints.push(endpoint);
            }
            updatedGroups = updateEndpointsInGroup(updatedGroups, updatedEndpoints);
        });
        dispatch(organizationActions.setGroups(updatedGroups));
    };

    const descriptionPaymentAddressInput = () => {
        return (
            <div>
                <span>
                    The ethereum address to which all payments will be processed for this group. See
                    Payment Address section &nbsp;
                </span>
                <a
                    href="https://dev.singularitynet.io/docs/products/DecentralizedAIPlatform/PublisherPortal/text-guide/"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    here
                </a>
                <span>&nbsp;and creating ethereum identity &nbsp;</span>
                <a
                    href="https://dev.singularitynet.io/docs/products/DecentralizedAIPlatform/UsedTechnologies/etcd/"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    page
                </a>
            </div>
        );
    };

    const descriptionETCDInput = () => {
        return (
            <div>
                <span>
                    Enter all the ETCD endpoints that will be used, separated by commas. Details
                    &nbsp;
                </span>
                <a
                    href="http://dev.singularitynet.io/docs/ai-developers/etcd/"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    here
                </a>
            </div>
        );
    };

    const HeaderGroup = () => {
        return (
            <>
                <Typography variant="h6">Groups / Region Settings</Typography>
                <Box className={classes.regionTypeContainer}>
                    <Typography className={classes.header}>Region Name</Typography>
                    <Typography className={classes.valueRegionType}>{name}</Typography>
                </Box>
            </>
        );
    };

    const ButtonGroup = () => {
        return (
            <Box className={classes.btnContainer}>
                <SNETButton
                    children={
                        showAdvancedSettings ? "hide advanced settings" : "show advanced settings"
                    }
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                />
            </Box>
        );
    };

    return (
        <Box className={classes.settingsContainer}>
            <HeaderGroup />
            <Box className={classes.settingsContentContainer}>
                <SNETTextfield
                    name="paymentAddress"
                    value={paymentAddress}
                    onChange={handlePaymentAddressChange}
                    label="Payment Address"
                    description={descriptionPaymentAddressInput()}
                    disabled={foundInBlockchain}
                    error={paymentAddressValidation}
                />
                <TagsInputField
                    name="id"
                    addTags={handleKeyEnterInTags}
                    label="ETCD Endpoint"
                    description={descriptionETCDInput()}
                    tagsEntered={paymentConfig.paymentChannelStorageClient.endpoints}
                    error={endpointValidation}
                />
            </Box>
            <AdvanceSettings
                show={showAdvancedSettings}
                groups={groups}
                groupIndex={groupIndex}
                group={group}
                foundInBlockchain={foundInBlockchain}
            />
            <ButtonGroup />
        </Box>
    );
};

export default Settings;
